import { FunctionComponent } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { desktopCSS, mobileCSS } from '@measures/responsive'
import { CueLivePost } from '@utils/cueLive'
import { formatDate, FormattedDate } from '@utils/date'
import translate from '@i18n'
import { bodyStyle } from '@widgets/Body/bodyStyle'
import FreeRatioImage from '@components/FreeRatioImage'
import JSONRenderer from '@components/JSONRenderer'
import SvgIcon from '@components/SvgIcon'

import VerticalLine from './VerticalLine'

interface PostProps extends Pick<
  CueLivePost,
  'id' | 'publishedAt' | 'icon' | 'event'
> {
  isPinned?: boolean
  accentColor?: string
  children: CueLivePost['values']
}

const imageConstraint = { width: 200, height: 24 }

export const StyledPost = styled.div<
  Pick<PostProps, 'isPinned' | 'accentColor'> & { isGoalPost?: boolean }
>`
  ${({
    theme: {
      colors: { strokeBrand, fillSport },
      spacing: { spacing12, spacing16, spacing24, spacing32 },
      measures: { outerPadding },
      utils: { opacity },
    },
    isPinned,
    isGoalPost,
    accentColor,
  }) => css`
    position: relative;

    padding-bottom: ${spacing24};
    padding-left: ${spacing12};

    ${desktopCSS(css`
      padding-bottom: ${spacing32};
      padding-left: ${spacing16};

      ${isGoalPost && `padding-right: ${spacing16}`};
    `)};

    &:first-child > ${VerticalLine} {
      top: 8px;

      height: calc(100% - 8px);
    }

    ${isPinned &&
    css`
      border: 2px solid ${accentColor ?? strokeBrand};
      border-radius: 4px;

      padding: ${spacing16};
      margin-bottom: ${spacing24};

      ${desktopCSS(css`
        padding: ${spacing24};
        margin-bottom: ${spacing32};
      `)};
    `}

    ${isGoalPost &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;

        background-color: ${opacity(fillSport, '1A')};

        height: 100%;
        width: 100%;

        ${!isPinned &&
        css`
          margin-top: -${spacing12};

          ${mobileCSS(css`
            margin-left: -${outerPadding.mobile};
            margin-right: -${outerPadding.mobile};
            width: calc(100% + 2 * ${outerPadding.mobile});
          `)};
        `}
      }
    `};
  `}
`

const PinWrapper = styled.div<Pick<PostProps, 'accentColor'>>`
  ${({
    theme: {
      colors: { fillBrand },
      spacing: { spacing4 },
    },
    accentColor,
  }) => css`
    position: absolute;
    right: -2px;
    top: -2px;
    display: flex;

    padding: ${spacing4};
    border-radius: 0px ${spacing4};

    background-color: ${accentColor ?? fillBrand};
  `}
`

const StyledDate = styled.div<Pick<PostProps, 'accentColor'>>`
  ${({
    theme: {
      typography: {
        catchword: {
          medium2: { bundledCSS: medium2Catchword },
        },
      },
      colors: { textBrand },
      spacing: { spacing8 },
    },
    accentColor,
  }) => css`
    color: ${accentColor ?? textBrand};
    ${medium2Catchword};
    display: flex;
    align-items: center;
    margin-bottom: ${spacing8};
  `}
`

const StyledIconWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-column-gap: ${spacing8};
    align-items: center;
    justify-items: flex-start;
    margin-bottom: ${spacing8};
  `}
`

const IconImage = styled.div``

const IconLabel = styled.div`
  ${({
    theme: {
      typography: {
        catchword: {
          medium2: { bundledCSS: medium2Catchword },
        },
      },
    },
  }) => css`
    ${medium2Catchword};
  `}
`

const StyledWidgetsWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing24 },
    },
  }) => css`
    ${bodyStyle}

    ${desktopCSS(css`
      grid-row-gap: ${spacing24};
    `)};
  `}
`

export const StyledDot = styled.div<Pick<PostProps, 'accentColor'>>`
  ${({
    theme: {
      colors: { strokeBrand },
    },
    accentColor,
  }) => css`
    position: absolute;
    left: -3px;

    background-color: ${accentColor ?? strokeBrand};
    width: 8px;
    height: 8px;
    border-radius: 50%;
  `};
`

const Post: FunctionComponent<PostProps> = ({
  isPinned,
  icon,
  publishedAt,
  accentColor,
  event,
  children,
}) => {
  const theme = useTheme()

  const hasIcon = !!icon?.image?.src
  const isGoalPost = event?.action === 'goal'

  return (
    <StyledPost
      isPinned={isPinned}
      accentColor={accentColor}
      isGoalPost={isGoalPost}
    >
      {!!isPinned && (
        <PinWrapper
          accentColor={accentColor}
          title={translate('cueLive.pinnedPost')}
        >
          <SvgIcon
            iconName="thumbtack"
            size={24}
            color={theme.colors.staticWhite}
          />
        </PinWrapper>
      )}
      {!isPinned && <VerticalLine accentColor={accentColor} />}
      <StyledDate accentColor={accentColor} suppressHydrationWarning={true}>
        {!isPinned && <StyledDot accentColor={accentColor} />}
        {(formatDate(new Date(publishedAt)) as FormattedDate)?.dateOrTime}
      </StyledDate>
      {hasIcon && (
        <StyledIconWrapper>
          <IconImage>
            <FreeRatioImage
              src={icon.image.src}
              alt={icon.label}
              originalDimensions={{
                width: icon.image.width,
                height: icon.image.height,
              }}
              constraints={{
                desktop: imageConstraint,
                tablet: imageConstraint,
                mobile: imageConstraint,
                mobileSmall: imageConstraint,
              }}
              crops={icon.image.crops ?? {}}
              {...(icon.imageDark?.src
                ? {
                    darkModeImage: {
                      src: icon.imageDark.src,
                      originalDimensions: {
                        width: icon.imageDark.width,
                        height: icon.imageDark.height,
                      },
                      crops: icon.imageDark.crops ?? {},
                    },
                  }
                : {})}
            />
          </IconImage>
          <IconLabel>{icon.label}</IconLabel>
        </StyledIconWrapper>
      )}
      <StyledWidgetsWrapper>
        <JSONRenderer>{children}</JSONRenderer>
      </StyledWidgetsWrapper>
    </StyledPost>
  )
}

export default Post
